import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentResidenceQuery } from '../../../../graphql/graphql';
import { Modal } from '../../../ui/Modal';
import { BackButtonIconEnum } from '../../../ui/PageHeader/types';
import {
  FacilitiesContent,
  FacilitiesItem,
  headerStyles,
  ModalContent,
  modalStyles,
  ParamsBlock,
  ParamsContent,
  ParamsItem,
  ParamsItemTitle,
  ParamsItemValue,
  ParamsModalItemsTitle
} from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';

interface ParamsModalProps {
  isOpen: boolean;
  onClose: () => void;
  residenceData: Partial<NonNullable<CurrentResidenceQuery['residence']>>;
  infoItems: {
    key: string;
    label: string;
    value?: string | number | null;
  }[];
}

export const ParamsModal: React.FC<ParamsModalProps> = memo(
  ({ residenceData, isOpen, onClose, infoItems }) => {
    const { t } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();

    const { name, technology, decoration, ceilingHeight, windowType, layout } =
      residenceData;

    const paramsList = useMemo(
      () => [
        ...infoItems,
        {
          key: 'technology',
          label: t('residences.technology'),
          value: technology?.name
        },
        {
          key: 'decoration',
          label: t('residences.decoration'),
          value: decoration?.name
        },
        {
          key: 'ceilingHeight',
          label: t('residences.ceiling_height'),
          value: ceilingHeight ? `${ceilingHeight} cm` : null
        },
        {
          key: 'windowType',
          label: t('residences.window_type'),
          value: windowType?.name
        },
        {
          key: 'layout',
          label: t('residences.layout'),
          value: layout?.name
        }
      ],
      [t, infoItems, technology, decoration, ceilingHeight, windowType, layout]
    );

    const modalTitle = isSmallOrLargeDesktop
      ? t('residences.params_modal_desktop_title', { name })
      : t('residences.params_modal_title');

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        modalId="residence-params"
        cypress="residence-params"
        title={modalTitle}
        modalStyles={modalStyles}
        headerIcon={BackButtonIconEnum.Close}
        closeButtonAlign="right"
        headerStyles={headerStyles}
        withAnimation={false}
      >
        <ModalContent>
          <ParamsBlock>
            <ParamsModalItemsTitle tag="title_3_desc">
              {t('residences.params_modal_content')}
            </ParamsModalItemsTitle>
            <ParamsContent>
              {paramsList.map(
                ({ key, label, value }) =>
                  value && (
                    <ParamsItem key={key}>
                      <ParamsItemTitle tag="body_1">{label}</ParamsItemTitle>
                      <ParamsItemValue tag="body_1">{value}</ParamsItemValue>
                    </ParamsItem>
                  )
              )}
            </ParamsContent>
          </ParamsBlock>

          {!!residenceData.facilities?.length && (
            <div>
              <ParamsModalItemsTitle tag="title_3_desc">
                {t('residences.facilities')}
              </ParamsModalItemsTitle>
              <FacilitiesContent>
                {residenceData.facilities?.map((item, index) => (
                  <FacilitiesItem key={`facility-${index}`} tag="body_1">
                    {item.name}
                  </FacilitiesItem>
                ))}
              </FacilitiesContent>
            </div>
          )}
        </ModalContent>
      </Modal>
    );
  }
);

ParamsModal.displayName = `ParamsModal`;
